import appConfig from '../config/app-config';
import tokenAwareFetcher from '../utils/tokenAwareFetcher';
import { ErrorField } from '../types/ErrorField';

const FUNDING_ENV = appConfig.apis.fundingApiEnv;
const fetcher = tokenAwareFetcher(FUNDING_ENV.address, FUNDING_ENV.key);

export type InstrumentProduct = 'LCA' | 'CDB' | 'DI';
export type RateType = 'PRE' | 'POS';
export type InstrumentStatus = 'DRAFT' | 'CUSTODY_REGISTERED' | 'COLLATERAL_REGISTERED' | 'FULLY_REGISTERED' | 'CANCELLED';
export type RegistrationType = 'CUSTODY' | 'COLLATERAL';
export type RegistrationStatus = 'PENDING' | 'SUCCESS' | 'FAILED';

export interface InstrumentRegistration {
    id: string;
    type: RegistrationType;
    typeFormatted: string;
    status: RegistrationStatus;
    statusFormatted: string;
    externalCode: string | null;
    tickerSymbol: string | null;
    registeredAt: string | null;
    createdAt: string;
}

export interface Instrument {
    id: string;
    product: InstrumentProduct;
    productFormatted: string;
    issueDate: string | null;
    dueDate: string;
    rateType: RateType | null;
    remunerationIndex: string | null;
    tax: string;
    taxGross: string | null;
    quantity: number;
    pu: string | null;
    puDegassed: string | null;
    financialValue: string | null;
    diFuturo: string | null;
    additionalSpread: string | null;
    commandNumber: number | null;
    platformId: string;
    status: InstrumentStatus;
    statusFormatted: string;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
    registrations: InstrumentRegistration[];
}

// Agregado Platform (corretoras) - criado em 2026-08-18 no backend, ver
// docs/api-contract.md secao "Plataformas (corretoras)". Desde 2026-08-19 a
// conta CETIP e por produto (Daycoval usa contas diferentes pra LCA e CDB) -
// nao existe mais um cetipAccount unico.
export interface Platform {
    id: string;
    name: string;
    cetipAccountLca: string | null;
    cetipAccountCdb: string | null;
    cetipAccountDi: string | null;
    active: boolean;
}

export interface CreateInstrumentItem {
    product: InstrumentProduct;
    issue_date: string;
    due_date: string;
    rate_type: RateType;
    remuneration_index?: string | null;
    tax: number;
    tax_gross?: number | null;
    quantity: number;
    pu: number;
    pu_degassed: number | null;
    financial_value: number;
    di_futuro?: number | null;
    additional_spread?: number | null;
    platform_id: string;
}

export interface UpdateInstrumentPayload {
    product?: InstrumentProduct;
    issue_date?: string;
    due_date?: string;
    rate_type?: RateType;
    remuneration_index?: string | null;
    tax?: number;
    tax_gross?: number | null;
    quantity?: number;
    pu?: number;
    pu_degassed?: number | null;
    financial_value?: number;
    di_futuro?: number | null;
    additional_spread?: number | null;
}

export interface UpdatePlatformPayload {
    name?: string;
    cetip_account_lca?: string | null;
    cetip_account_cdb?: string | null;
    cetip_account_di?: string | null;
    clear_cetip_account_lca?: boolean;
    clear_cetip_account_cdb?: boolean;
    clear_cetip_account_di?: boolean;
}

// Resultado da conferência do PU Desagiado (GET /instruments/{id}/pu-desagiado-conference)
// - recalcula o PU Desagiado esperado (fórmula de mercado, só papéis PRE) e
// compara com o valor recebido da corretora. É alerta, não bloqueia nada -
// ver docs/context/business-context.md §8.2 no repo funding.
export interface PuDesagiadoCheckResult {
    applicable: boolean;
    reason?: string;
    expectedPuDegassed?: string;
    reportedPuDegassed?: string;
    businessDays?: number;
    relativeDifference?: string;
    diverges?: boolean;
    // Taxa cheia implícita no PU Desagiado reportado, independente de o
    // instrumento ter taxa cheia informada (inversão da fórmula, ver
    // implied_tax_gross_pre/pos no backend) - 2026-09-10.
    impliedTaxGross?: string;
}

// Item de resultado do POST /instruments/pu-desagiado-conference (conferência
// ANTES de salvar) - mesma forma do GET, mais o índice do item no lote
// enviado (não tem id ainda, o instrumento não foi criado).
export interface PuDesagiadoPreviewItem {
    index: number;
    applicable: boolean;
    reason?: string;
    expectedPuDegassed?: string;
    reportedPuDegassed?: string;
    businessDays?: number;
    relativeDifference?: string;
    diverges?: boolean;
    impliedTaxGross?: string;
}

export interface PageListResult {
    items: Instrument[];
    page: number;
    size: number;
    total: number;
}

export type ApiResult<T> =
    | { ok: true; data: T }
    | { ok: false; status: number; errors: ErrorField[]; message: string };

const parseErrorBody = async (response: Response): Promise<{ errors: ErrorField[]; message: string }> => {
    try {
        const json = await response.json();
        const errors: ErrorField[] = json.errors || [];
        const message = errors[0]?.message || `Erro ao processar requisição (status ${response.status})`;
        return { errors, message };
    } catch {
        return { errors: [], message: `Erro ao processar requisição (status ${response.status})` };
    }
};

export const fundingApi = {
    listInstruments: async (page: number, size: number, includeCancelled: boolean): Promise<ApiResult<PageListResult>> => {
        const params = new URLSearchParams({
            page: String(page),
            size: String(size),
            includeCancelled: String(includeCancelled),
        });
        const response = await fetcher.get('/instruments/page-list', params);
        if (!response.ok) {
            const { errors, message } = await parseErrorBody(response);
            return { ok: false, status: response.status, errors, message };
        }
        const json = await response.json();
        return {
            ok: true,
            data: {
                items: json.data || [],
                page: json.page,
                size: json.size,
                total: json.total,
            },
        };
    },

    createInstruments: async (items: CreateInstrumentItem[], createdBy: string): Promise<ApiResult<{ createdIds: string[] }>> => {
        const response = await fetcher.post('/instruments', { items, created_by: createdBy });
        if (!response.ok) {
            const { errors, message } = await parseErrorBody(response);
            return { ok: false, status: response.status, errors, message };
        }
        const json = await response.json();
        return { ok: true, data: { createdIds: json.data?.createdIds || [] } };
    },

    updateInstrument: async (id: string, changes: UpdateInstrumentPayload): Promise<ApiResult<Instrument>> => {
        const response = await fetcher.put(`/instruments/${id}`, changes);
        if (!response.ok) {
            const { errors, message } = await parseErrorBody(response);
            return { ok: false, status: response.status, errors, message };
        }
        const json = await response.json();
        return { ok: true, data: json.data };
    },

    cancelInstrument: async (id: string): Promise<ApiResult<{ instrumentId: string; status: string }>> => {
        const response = await fetcher.delete(`/instruments/${id}`);
        if (!response.ok) {
            const { errors, message } = await parseErrorBody(response);
            return { ok: false, status: response.status, errors, message };
        }
        const json = await response.json();
        return { ok: true, data: json.data };
    },

    // Endpoint único pros dois tipos de registro (custódia B3 / lastro Dimensa),
    // despachado por `type` no body - antes eram duas rotas com verbo no path
    // (register-custody/register-collateral), trocadas em 2026-09-08 pra bater
    // com a regra 91-rest-api-standards ("NEVER use verbs in route paths").
    createRegistration: async (id: string, type: RegistrationType): Promise<ApiResult<InstrumentRegistration>> => {
        const response = await fetcher.post(`/instruments/${id}/registrations`, { type });
        if (!response.ok) {
            const { errors, message } = await parseErrorBody(response);
            return { ok: false, status: response.status, errors, message };
        }
        const json = await response.json();
        return { ok: true, data: json.data };
    },

    checkPuDesagiado: async (id: string): Promise<ApiResult<PuDesagiadoCheckResult>> => {
        const response = await fetcher.get(`/instruments/${id}/pu-desagiado-conference`);
        if (!response.ok) {
            const { errors, message } = await parseErrorBody(response);
            return { ok: false, status: response.status, errors, message };
        }
        const json = await response.json();
        return { ok: true, data: json.data };
    },

    // Mesma conferência do checkPuDesagiado acima, mas ANTES de salvar - manda
    // o lote inteiro (ainda sem id, não foi criado) e recebe o resultado por
    // item. Não bloqueia nada; é usado pra alertar o usuário na tela antes do
    // clique em Salvar (decisão do negócio, 2026-09-09, ao achar boletas do
    // Daycoval com PU divergente na planilha).
    previewPuDesagiado: async (items: CreateInstrumentItem[]): Promise<ApiResult<PuDesagiadoPreviewItem[]>> => {
        const response = await fetcher.post('/instruments/pu-desagiado-conference', {
            items: items.map((item) => ({
                product: item.product,
                rate_type: item.rate_type,
                issue_date: item.issue_date,
                due_date: item.due_date,
                tax: item.tax,
                tax_gross: item.tax_gross,
                pu: item.pu,
                pu_degassed: item.pu_degassed,
                di_futuro: item.di_futuro,
            })),
        });
        if (!response.ok) {
            const { errors, message } = await parseErrorBody(response);
            return { ok: false, status: response.status, errors, message };
        }
        const json = await response.json();
        return { ok: true, data: json.data || [] };
    },

    listPlatforms: async (): Promise<ApiResult<Platform[]>> => {
        const response = await fetcher.get('/platforms');
        if (!response.ok) {
            const { errors, message } = await parseErrorBody(response);
            return { ok: false, status: response.status, errors, message };
        }
        const json = await response.json();
        return { ok: true, data: json.data || [] };
    },

    createPlatform: async (
        name: string,
        cetipAccountLca: string | null,
        cetipAccountCdb: string | null,
        cetipAccountDi: string | null = null,
    ): Promise<ApiResult<{ platformId: string }>> => {
        const response = await fetcher.post('/platforms', {
            name,
            cetip_account_lca: cetipAccountLca,
            cetip_account_cdb: cetipAccountCdb,
            cetip_account_di: cetipAccountDi,
        });
        if (!response.ok) {
            const { errors, message } = await parseErrorBody(response);
            return { ok: false, status: response.status, errors, message };
        }
        const json = await response.json();
        return { ok: true, data: json.data };
    },

    updatePlatform: async (id: string, changes: UpdatePlatformPayload): Promise<ApiResult<Platform>> => {
        const response = await fetcher.put(`/platforms/${id}`, changes);
        if (!response.ok) {
            const { errors, message } = await parseErrorBody(response);
            return { ok: false, status: response.status, errors, message };
        }
        const json = await response.json();
        return { ok: true, data: json.data };
    },

    deactivatePlatform: async (id: string): Promise<ApiResult<{ platformId: string; active: boolean }>> => {
        const response = await fetcher.delete(`/platforms/${id}`);
        if (!response.ok) {
            const { errors, message } = await parseErrorBody(response);
            return { ok: false, status: response.status, errors, message };
        }
        const json = await response.json();
        return { ok: true, data: json.data };
    },
};

export default fundingApi;
